import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

import useBranch from 'client/hooks/useBranch';

import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrModalV2, { MODAL_THEME_SLIDE_UP } from 'client/components/SrModalV2/SrModalV2';
import SrUtilityActivationEditItemModal from 'client/components/SrUtilityActivationEditItemModal';
import SrUtilityActivationInternetPurchasedOutsideModal from 'client/components/SrUtilityActivationInternetPurchasedOutsideModal';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrUtilityActivationStatusModalHeader from './SrUtilityActivationStatusModalHeader';
import SrUtilityActivationStatusModalSection from './SrUtilityActivationStatusModalSection';
import SrUtilityActivationStatusModalDelete from './SrUtilityActivationStatusModalDelete';

const SrUtilityActivationStatusModal = ({
  activation,
  utilityPartner,
  utilityTypes,
  onClose,
  onArchive,
  onItemEditCallback,
  onRequiredUtilityTypeToggle,
}) => {
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentPreviewURL, setDocumentPreviewURL] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { utilityUser } = useBranch({
    utilityUser: ['utilityUser'],
  });

  const handleDocumentModalOpen = (item) => {
    setIsDocumentModalOpen(true);
    setDocumentPreviewURL(item.proofOfPurchaseUrl);
  };

  const handleDocumentModalClose = () => {
    setIsDocumentModalOpen(false);
    setDocumentPreviewURL(null);
  };

  const handleEditCallback = () => {
    setItemToEdit(null);
    onItemEditCallback();
  };

  const handleRequiredUtilityTypeToggle = async (utilityTypeId) => {
    setIsLoading(true);
    onRequiredUtilityTypeToggle(utilityTypeId);
  };

  return (
    <div>
      <SrDarkModal onClose={onClose}>
        <SrModalContent width={720} innerStyle={{ padding: 0 }}>
          <SrUtilityActivationStatusModalHeader activation={activation} utilityUser={utilityUser} />
          {isLoading && (
            <div
              className="loading-ellipses"
              style={{
                margin: '0 auto',
                padding: 0,
              }}
            />
          )}
          {!isLoading &&
            utilityTypes.map((utilityType) => (
              <SrUtilityActivationStatusModalSection
                key={utilityType.id}
                utilityType={utilityType}
                utilityPartner={utilityPartner}
                activation={activation}
                leaseStartDate={activation.leaseStartDate}
                onRequiredUtilityTypeToggle={handleRequiredUtilityTypeToggle}
                onEditItem={setItemToEdit}
                onDocumentPreview={handleDocumentModalOpen}
              />
            ))}
          <SrUtilityActivationStatusModalDelete onArchive={onArchive} />
        </SrModalContent>
      </SrDarkModal>

      {isDocumentModalOpen && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth="90vw"
          contentMaxHeight="90vh"
          contentPadding="50px 25px 30px"
          onClose={handleDocumentModalClose}
          theme={MODAL_THEME_SLIDE_UP}
          centerContent
        >
          <SrS3FileDisplay url={documentPreviewURL} maxHeight="85vh" withRotateButton />
        </SrModalV2>
      )}

      {itemToEdit &&
        (itemToEdit.type?.id === UTILITY_TYPE_ID_INTERNET ? (
          <SrUtilityActivationInternetPurchasedOutsideModal
            utilityActivationItem={itemToEdit}
            onSubmitCallback={handleEditCallback}
            onClose={() => setItemToEdit(null)}
          />
        ) : (
          <SrUtilityActivationEditItemModal
            address={activation?.address?.display}
            utilityActivationItem={itemToEdit}
            onClose={() => setItemToEdit(null)}
            onEditCallback={handleEditCallback}
          />
        ))}
    </div>
  );
};

SrUtilityActivationStatusModal.propTypes = {
  activation: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    leaseStartDate: PropTypes.string.isRequired,
    address: PropTypes.shape({
      display: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  utilityPartner: PropTypes.object,
  utilityTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      iconUrl: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onItemEditCallback: PropTypes.func.isRequired,
  onRequiredUtilityTypeToggle: PropTypes.func.isRequired,
};

export default SrUtilityActivationStatusModal;
